import {
  Bar,
  BarChart as RechartsBar,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartFrame, ChartTooltip, type TableColumn } from "./chart-frame";
import { AXIS_PROPS, CHROME, EMPHASIS, GRID_PROPS, MARK, seriesColor } from "./tokens";

export interface CategoryDatum {
  label: string;
  value: number;
}

interface CategoryBarProps {
  title: string;
  note?: string;
  data: CategoryDatum[];
  /** Column header for the value column in the table view. */
  valueHeader: string;
  loading?: boolean;
  error?: string | null;
  /** Highlights one category in the accent and greys the rest. */
  emphasis?: string;
  /** Horizontal is the right call for long category names (team names). */
  orientation?: "vertical" | "horizontal";
  height?: number;
  className?: string;
  emptyBody?: string;
}

/**
 * One nominal series (teams, designations, leave types). Every bar takes the
 * SAME colour — colouring nominal bars by their own value would re-encode the
 * bar length as hue and spend the identity channel on nothing.
 *
 * With `emphasis`, the form becomes the emphasis pattern instead: one bar in the
 * accent, the rest recessive. That is the honest form when the story is about
 * one category rather than the spread.
 */
export function CategoryBarChart({
  title,
  note,
  data,
  valueHeader,
  loading,
  error,
  emphasis,
  orientation = "horizontal",
  height = 240,
  className,
  emptyBody,
}: CategoryBarProps) {
  const columns: TableColumn[] = [
    { key: "label", header: "Category" },
    { key: "value", header: valueHeader, numeric: true },
  ];
  const rows = data.map((d) => ({ label: d.label, value: d.value.toLocaleString("en-IN") }));

  // A single series needs no legend box: the title already names what is plotted.
  const barColor = (label: string) =>
    emphasis ? (label === emphasis ? EMPHASIS.focus : EMPHASIS.rest) : seriesColor(0);

  // Horizontal bars need room for the longest category label.
  const labelWidth = Math.min(
    150,
    Math.max(72, ...data.map((d) => d.label.length * 6.6 + 12)),
  );

  return (
    <ChartFrame
      title={title}
      {...(note ? { note } : {})}
      table={{ columns, rows }}
      {...(loading ? { loading } : {})}
      {...(error ? { error } : {})}
      empty={!loading && !error && data.length === 0}
      {...(emptyBody ? { emptyBody } : {})}
      height={height}
      {...(className ? { className } : {})}
    >
      <ResponsiveContainer width="100%" height={height}>
        {orientation === "horizontal" ? (
          <RechartsBar
            data={data}
            layout="vertical"
            margin={{ top: 4, right: 36, bottom: 4, left: 0 }}
            barCategoryGap={MARK.gap * 3}
          >
            <CartesianGrid {...GRID_PROPS} horizontal={false} vertical />
            <XAxis type="number" {...AXIS_PROPS} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="label"
              width={labelWidth}
              {...AXIS_PROPS}
              axisLine={false}
            />
            <Tooltip
              cursor={{ fill: CHROME.grid, fillOpacity: 0.5 }}
              content={<ChartTooltip />}
            />
            <Bar
              dataKey="value"
              name={valueHeader}
              radius={MARK.radiusRight}
              maxBarSize={MARK.maxBarSize}
              isAnimationActive={false}
            >
              {data.map((d) => (
                <Cell key={d.label} fill={barColor(d.label)} />
              ))}
              {/* Value at the tip — the direct label that lets the reader skip
                  the axis entirely. */}
              <LabelList
                dataKey="value"
                position="right"
                offset={8}
                style={{ fill: "rgb(var(--ink-soft))", fontSize: 11, fontVariantNumeric: "tabular-nums" }}
              />
            </Bar>
          </RechartsBar>
        ) : (
          <RechartsBar
            data={data}
            margin={{ top: 20, right: 8, bottom: 4, left: -16 }}
            barCategoryGap={MARK.gap * 3}
          >
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" {...AXIS_PROPS} interval={0} />
            <YAxis {...AXIS_PROPS} allowDecimals={false} axisLine={false} />
            <Tooltip cursor={{ fill: CHROME.grid, fillOpacity: 0.5 }} content={<ChartTooltip />} />
            <Bar
              dataKey="value"
              name={valueHeader}
              radius={MARK.radiusUp}
              maxBarSize={MARK.maxBarSize}
              isAnimationActive={false}
            >
              {data.map((d) => (
                <Cell key={d.label} fill={barColor(d.label)} />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                offset={6}
                style={{ fill: "rgb(var(--ink-soft))", fontSize: 11, fontVariantNumeric: "tabular-nums" }}
              />
            </Bar>
          </RechartsBar>
        )}
      </ResponsiveContainer>
    </ChartFrame>
  );
}
